import React, { useState, useEffect } from 'react';
import { ROLES } from '../utils/permissions';
import api from '../services/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Typography
} from '@mui/material';
import { Close as CloseIcon, Add as AddIcon, Delete as DeleteIcon, Warning as WarningIcon, Person as PersonIcon } from '@mui/icons-material';
import '../styles/components/modal.css';
import { SkeletonList } from './SkeletonLoader';

const ManageUsers = () => {
  const [users, setUsers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    role: ROLES.COORDINATOR,
    userType: 'system' // 'system' or 'interviewTaker'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [interviewTakers, setInterviewTakers] = useState([]);


  useEffect(() => {
    fetchUsers();
    fetchInterviewTakers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');

      const apiUsers = await api.getUsers();
      setUsers(apiUsers || []);
    } catch (error) {
      console.error('Failed to load users from API', error);
      setError('Failed to load users from server');
    } finally {
      setLoading(false);
    }
  };

  const getUsersByRole = (role) => {
    return users.filter(user => user.role === role);
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await api.request(`/api/v1/users/${userToDelete.id}`, { method: 'DELETE' });
      
      // Refresh users list
      await fetchUsers();
      
      setShowDeleteModal(false);
      setUserToDelete(null);
      setError(null);
    } catch (error) {
      console.error('Failed to delete user', error);
      setError(error.response?.data?.detail || 'Failed to delete user');
      setShowDeleteModal(false);
      setUserToDelete(null);
    }
  };

  const fetchInterviewTakers = async () => {
    try {
      const takers = await api.request('/api/v1/interview-takers');
      setInterviewTakers(takers || []);
    } catch (error) {
      console.error('Failed to load interview takers from API', error);
      setError(prev => prev || 'Failed to load interview takers from server');
    }
  };



  const handleDeleteTaker = (takerName) => {
    const taker = interviewTakers.find(t => t.name === takerName);
    if (!taker) return;

    const remove = async () => {
      try {
        await api.request(`/api/v1/interview-takers/${taker.id}`, { method: 'DELETE' });
        await fetchInterviewTakers();
      } catch (error) {
        console.error('Failed to delete interview taker', error);
        setError('Failed to delete interview taker');
      }
    };

    void remove();
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');
      
      if (newUser.userType === 'interviewTaker') {
        // Add to interview takers via backend API
        const existing = interviewTakers.find(
          t => t.name.toLowerCase() === newUser.username.trim().toLowerCase(),
        );
        if (existing) {
          setError('Interview taker already exists');
          setLoading(false);
          return;
        }

        try {
          await api.request('/api/v1/interview-takers', {
            method: 'POST',
            body: JSON.stringify({ name: newUser.username.trim(), email: newUser.email || null }),
          });
          await fetchInterviewTakers();
        } catch (apiError) {
          console.error('Failed to add interview taker', apiError);
          setError(apiError.message || 'Failed to add interview taker via server');
          setLoading(false);
          return;
        }
      } else {
        // Add to system users via backend API (/api/v1/auth/register)
        try {
          await api.register({
            name: newUser.username,
            email: newUser.email,
            password: newUser.password,
            role: newUser.role,
          });
          await fetchUsers();
        } catch (apiError) {
          setError(apiError.message || 'Failed to add user via server');
          setLoading(false);
          return;
        }
      }
      
      setShowAddModal(false);
      setNewUser({ username: '', email: '', password: '', role: ROLES.COORDINATOR, userType: 'system' });
    } catch (error) {
      setError('Failed to add user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="manage-users">
      <div className="page-header">
        <h1>Manage Users</h1>
        <Button 
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddModal(true)}
          sx={{
            background: 'linear-gradient(135deg, #62D196 0%, #4ade80 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)',
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(98, 209, 150, 0.4)'
            },
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          Add New User
        </Button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Admin Users */}
      <div className="role-section">
        <h3>Admin Users ({getUsersByRole(ROLES.ADMIN).length})</h3>
        {loading ? (
          <SkeletonList items={3} />
        ) : (
          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {getUsersByRole(ROLES.ADMIN).map(user => (
                <tr key={user.id}>
                  <td>{user.name || user.username}</td>
                  <td>{user.email}</td>
                  <td><span className="role-badge role-admin">{user.role}</span></td>
                  <td>
                    <Button 
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteUser(user)}
                      sx={{ textTransform: 'none' }}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
            {getUsersByRole(ROLES.ADMIN).length === 0 && (
              <p className="no-users">No admin users found</p>
            )}
          </div>
        )}
      </div>

      {/* Coordinator Users */}
      <div className="role-section">
        <h3>Coordinator Users ({getUsersByRole(ROLES.COORDINATOR).length})</h3>
        {loading ? (
          <SkeletonList items={3} />
        ) : (
          <div className="users-table">
            <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getUsersByRole(ROLES.COORDINATOR).map(user => (
                <tr key={user.id}>
                  <td>{user.name || user.username}</td>
                  <td>{user.email}</td>
                  <td><span className="role-badge role-coordinator">{user.role}</span></td>
                  <td>
                    <Button 
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteUser(user)}
                      sx={{ textTransform: 'none' }}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
            {getUsersByRole(ROLES.COORDINATOR).length === 0 && (
              <p className="no-users">No coordinator users found</p>
            )}
          </div>
        )}
      </div>

      {/* BD Users */}
      <div className="role-section">
        <h3>BD Users ({getUsersByRole(ROLES.BD).length})</h3>
        {loading ? (
          <SkeletonList items={3} />
        ) : (
          <div className="users-table">
            <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {getUsersByRole(ROLES.BD).map(user => (
                <tr key={user.id}>
                  <td>{user.name || user.username}</td>
                  <td>{user.email}</td>
                  <td><span className="role-badge role-bd">{user.role}</span></td>
                  <td>
                    <Button 
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteUser(user)}
                      sx={{ textTransform: 'none' }}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
            </table>
            {getUsersByRole(ROLES.BD).length === 0 && (
              <p className="no-users">No BD users found</p>
            )}
          </div>
        )}
      </div>

      {/* Interview Takers */}
      <div className="role-section">
        <h3>Interview Takers ({interviewTakers.length})</h3>
        {loading ? (
          <SkeletonList items={3} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {interviewTakers.map(taker => (
              <div
                key={taker.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  backgroundColor: '#f8fffe'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <PersonIcon sx={{ color: '#62D196' }} />
                  <span>{taker.name}</span>
                </div>
                <Button 
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDeleteTaker(taker)}
                  sx={{ textTransform: 'none' }}
                >
                  Delete
                </Button>
              </div>
            ))}
            {interviewTakers.length === 0 && (
              <p className="no-users">No interview takers found</p>
            )}
          </div>
        )}
      </div>

      <Dialog 
        open={showAddModal} 
        onClose={() => setShowAddModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        <DialogTitle 
          sx={{
            background: 'linear-gradient(135deg, #62D196 0%, #4ade80 100%)',
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 600
          }}
        >
          Add New User
          <IconButton 
            onClick={() => setShowAddModal(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <form onSubmit={handleAddUser}>
          <DialogContent sx={{ padding: '32px' }}>
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel>User Type</InputLabel>
              <Select
                value={newUser.userType}
                onChange={(e) => setNewUser({...newUser, userType: e.target.value})}
                label="User Type"
              >
                <MenuItem value="system">System User</MenuItem>
                <MenuItem value="interviewTaker">Interview Taker</MenuItem>
              </Select>
            </FormControl>
            
            <TextField
              label={newUser.userType === 'interviewTaker' ? 'Full Name' : 'Username'}
              value={newUser.username}
              onChange={(e) => setNewUser({...newUser, username: e.target.value})}
              required
              fullWidth
              margin="normal"
              variant="outlined"
            />
            
            {newUser.userType === 'system' && (
              <>
                <TextField
                  label="Email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  required
                  fullWidth
                  margin="normal"
                  variant="outlined"
                />
                
                <TextField
                  label="Password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  required
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  inputProps={{ minLength: 6 }}
                />
                
                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                    label="Role"
                  >
                    <MenuItem value={ROLES.COORDINATOR}>Coordinator</MenuItem>
                    <MenuItem value={ROLES.ADMIN}>Admin</MenuItem>
                    <MenuItem value={ROLES.BD}>BD User</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
          </DialogContent>
          
          <DialogActions sx={{ padding: '20px 24px', background: '#f8fafc' }}>
            <Button 
              onClick={() => setShowAddModal(false)}
              variant="outlined"
              sx={{ textTransform: 'none' }}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                background: 'linear-gradient(135deg, #62D196 0%, #4ade80 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4ade80 0%, #22c55e 100%)'
                },
                textTransform: 'none'
              }}
            >
              {loading ? 'Adding...' : (newUser.userType === 'interviewTaker' ? 'Add Interview Taker' : 'Add User')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog 
        open={showDeleteModal} 
        onClose={() => setShowDeleteModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            textAlign: 'center'
          }
        }}
      >
        <DialogTitle sx={{ paddingTop: '32px' }}>
          <WarningIcon sx={{ fontSize: 48, color: '#f56565', marginBottom: 2 }} />
          <Typography variant="h6" component="div">
            Confirm Delete
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" color="text.secondary">
            Are you sure you want to delete user <strong>{userToDelete?.name || userToDelete?.username}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ padding: '20px 24px', justifyContent: 'center', gap: 2 }}>
          <Button 
            onClick={() => setShowDeleteModal(false)}
            variant="outlined"
            sx={{ textTransform: 'none', minWidth: '100px' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete}
            variant="contained"
            color="error"
            sx={{ textTransform: 'none', minWidth: '100px' }}
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>


    </div>
  );
};

export default ManageUsers;